import React, { useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import searchIcon from "../../assets/icons/search.svg";
import shareIcon from "../../assets/icons/share.svg";
import Layout from "../../components/Layout";
import { useStores } from "../../use.store";
import NoResultFoundComponent from "../../components/NoResultFoundComponent";

const ResourcesPage = () => {
  const { mapStore, mainStore } = useStores();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState();

  const filteredInfo = mapStore?.information?.filter((item) => {
    if (item.categoryName !== "Resources") {
      return false;
    }

    if (!searchValue) return true;

    const search = searchValue?.toLowerCase();
    const nameMatch = item?.name?.toLowerCase().includes(search);

    return nameMatch;
  });

  const itemsPerPage = 9;
  const totalPages = Math.ceil(filteredInfo.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    filteredInfo?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPrevious = () => goToPage(currentPage - 1);
  const goToNext = () => goToPage(currentPage + 1);

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
            currentPage === i
              ? "bg-[#215732] text-white"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          {i}
        </button>,
      );
    }
    return pages;
  };

  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef(null);
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <Layout>
      <div className="flex flex-col bg-[#215732] text-white px-4 py-6 h-[165px] items-center justify-center">
        <span className="text-[32px] font-medium mb-2">
          {mainStore.homeMenuResourcesLabel ?? "Resources"}
        </span>
        <div className="flex flex-row">
          <div class="flex items-center rounded-full overflow-hidden w-full bg-[#f8f9fc]">
            <div class="pl-4 text-gray-700"></div>
            <img src={searchIcon} className="aspect-square w-4 h-4" />
            <input
              type="text"
              placeholder="Search"
              className="flex-1 px-3 py-2 bg-[#f8f9fc] placeholder-[#75787B80] text-black focus:outline-none"
              ref={searchRef}
              onFocus={handleFocus}
              onBlur={handleBlur}
              value={searchValue}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchValue(e.target.value);
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}

      {isFocused && !searchValue ? (
        <></>
      ) : (
        <div className="flex flex-col py-9 gap-y-4 overflow-y-auto">
          {currentItems.map((data) => (
            <a href={data.description ?? "#"} target="_blank">
              <div className="flex flex-row border-b border-b-2 border-b-[#C0CCDA] py-2">
                <div className="flex flex-row w-full justify-between items-center px-4">
                  <span className="text-base text-black">{data.name}</span>
                  {data.description && (
                    <a href={data.description} target="_blank">
                      <img src={shareIcon} />
                    </a>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      <NoResultFoundComponent
        isFocused={isFocused}
        searchValue={searchValue}
        currentItems={currentItems}
      />
      <div className="flex flex-1" />

      <div className="flex justify-center items-center gap-2 mb-2">
        {/* First Page */}
        <button
          onClick={goToFirstPage}
          disabled={currentPage === 1}
          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>

        {/* Previous Page */}
        <button
          onClick={goToPrevious}
          disabled={currentPage === 1}
          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page Numbers */}
        {renderPageNumbers()}

        {/* Next Page */}
        <button
          onClick={goToNext}
          disabled={currentPage === totalPages}
          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Last Page */}
        <button
          onClick={goToLastPage}
          disabled={currentPage === totalPages}
          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </Layout>
  );
};

export default ResourcesPage;
